import { settingActions } from "../redux/setting";
import { store } from "../redux";
import { SignalRCore } from "./signalR/core";
import { hubs } from "./signalR/config";

function Api({ url, method, data, signal, token }) {
  let headers = {
    "Content-Type": "application/json; charset=utf-8",
    Accept: "application/json",
    ProjectAssistantHub: SignalRCore.connectionId(hubs.projectAssistant) || undefined,
  };

  if (token) {
    headers["Authorization"] = "Bearer " + token;
  }

  return new Promise((resolve, reject) => {
    fetch(url, {
      method: method || "GET",
      body: JSON.stringify(data),
      signal,
      headers,
    })
      .then((response) => {
        if (response.ok) {
          resolve(response);
        } else {
          switch (response.status) {
            case 401:
              // toast.warn("You are not logged in! Redirecting to login page...");
              console.warn("You are not logged in, redirecting to login page");
              store.dispatch(settingActions.logOut());
              break;
            case 404:
              console.error("Object not found url: " + url);
              break;
            case 500:
              console.error("Internal server error url: " + url);
              break;
            default:
              console.error("Some error occured url: " + url);
              break;
          }

          reject(response);
        }
      })
      .catch((error) => {
        //this will be invoked mostly for network errors
        console.error(error);
        reject(error);
      });
  });
}

export const api = {
  /**
   * @param url {string}
   * @param [queryParams] {Object<string, string|number>}
   * @returns {Promise<Response>}
   */
  get: (url, queryParams, token) => {
    return Api({ url: url + parseParams(queryParams), token });
  },
  /**
   * @param url
   * @param [data]
   * @param [queryParams]
   * @returns {Promise<Response>}
   */
  post: (url, data, queryParams) => {
    return Api({ url: url + parseParams(queryParams), method: "POST", data });
  },

  /**
   * @param url
   * @param [queryParams]
   * @returns {Promise<unknown>}
   */
  delete: (url, queryParams) => {
    return Api({ url: url + parseParams(queryParams), method: "DELETE" });
  },

  /**
   * @param url {string}
   * @param [queryParams] {Object<string, string|number>}
   * @returns {{abort: () => void, request: Promise<unknown>}}
   */
  getWithAbort: (url, queryParams) => {
    const controller = new AbortController();
    const signal = controller.signal;

    return {
      abort: () => controller.abort(),
      request: Api({ url: url + parseParams(queryParams), method: "GET", signal }),
    };
  },
};

export const apiUrls = {
  ChangeDatabase: "/api/Auth/ChangeDatabase",
  Projects: "/api/Project/Projects",
  ProcessProgress: "/api/Project/ProcessProgress",
  GetAllOrSearchedProjects: "/api/Project/GetAllOrSearchedProjects",
  StartProjectSimulation: "/api/Project/StartProjectSimulation",
  DeleteSimulation: "/api/Project/DeleteSimulation",
  projectGantt: "/api/Project/ProjectBryntumGantt",
  UpdateProjectStartDate: "/api/Project/UpdateProjectStartDate",
  GetRidderResourcesAndCalendars: "/api/Resource/GetRidderResourcesAndCalendars",
  SyncProject: "/api/Project/SyncProject",
  ProjectSyncList: "/api/ProjectSyncMessage/ProjectSync",
  UpdateMultipleProjectLines: "/api/ProjectLine/UpdateMultipleProjectLines",
  AddProjectLine: "/api/ProjectLine/AddProjectLine",
  CorrectDependenciesForSplitTask: "/api/ProjectLine/CorrectDependenciesForSplitTask",
  DeleteProjectLines: "/api/ProjectLine/DeleteProjectLines",
  ProjectSyncMessages: "/api/ProjectSyncMessage/ProjectSyncMessages",
  UpdateOrAddProjectLineBindings: "/api/ProjectLine/UpdateOrAddProjectLineBindings",
  RemoveProjectLineBinding: "/api/ProjectLine/RemoveProjectLineBinding",
  UpdateOrAddAssignments: "/api/Resource/UpdateOrAddAssignments",
  UpdateOrAddExplicitAssignments: "/api/Resource/UpdateOrAddExplicitAssignments",
  RemoveAssignment: "/api/ProjectLine/RemoveAssignment",
  GetBaselineProjects: "/api/Project/GetBaselineProjects",
  TotalProjectsOverview: "/api/Project/TotalProjectsOverviewBryntumGantt",
  DepartmentOverviewBryntumGantt: "/api/Project/DepartmentOverviewBryntumGantt",
  ConvertRootComponentToBryntumTask: "/api/Template/ConvertRootComponentToBryntumTask",
  CreateNewProject: "/api/Project/CreateNewProject",
  CreateNewOrderAsProject: "/api/Project/CreateNewOrderAsProject",
  ArchiveSimulation: "/api/Project/ArchiveSimulation",
  CompleteSimulation: "/api/Project/CompleteSimulation",
  GetSettings: "/api/Setting/getSettings",
  SaveSettings: "/api/Setting/saveSettings",
  DeleteSetting: "/api/Setting/DeleteSetting",
  UpdateMultipleBaseTemplateLines: "/api/Template/UpdateMultipleBaseTemplateLines",
  UpdateMultipleSubTemplateLines: "/api/Template/UpdateMultipleSubTemplateLines",
  TemplateBryntumGantt: "/api/Template/TemplateBryntumGantt",
  TemplateComponentBryntumGantt: "/api/Template/TemplateComponentBryntumGantt",
  Templates: "/api/Template/Templates",
  TemplateComponents: "/api/Template/TemplateComponents",
  CreateNewTemplate: "/api/Template/CreateNewTemplate",
  CreateNewRootComponent: "/api/Template/CreateNewRootComponent",
  AddTemplateComponent: "/api/Template/AddTemplateComponent",
  AddTemplateElement: "/api/Template/AddTemplateElement",
  DeleteTemplateComponents: "/api/Template/DeleteTemplateComponents",
  DeleteTemplateElements: "/api/Template/DeleteTemplateElements",
  UpdateOrAddElementBinding: "/api/Template/UpdateOrAddElementBinding",
  RemoveElementBinding: "/api/Template/RemoveElementBinding",
  UpdateOrAddComponentBinding: "/api/Template/UpdateOrAddComponentBinding",
  RemoveComponentBinding: "/api/Template/RemoveComponentBinding",
  Departments: "/api/BaseData/Departments",
  WorkActivityGroups: "/api/BaseData/WorkActivityGroups",
  WorkActivities: "/api/BaseData/WorkActivities",
  PersonalViewSettings: "/api/Setting/PersonalView",
  GetUserData: "/api/Setting/GetUserData",
  SyncLogStatus: "/api/ProjectSyncMessage/SyncLogStatus",
  Login: "/api/Auth/Login",
  RefreshToken: "/api/Auth/RefreshToken",
  Logout: "/api/Auth/Logout",
  GetProjectCategories: "/api/Project/GetProjectCategories",
  GetOrderCategories: "/api/Project/GetOrderCategories",
  HistogramDataByDepartment: "/api/Project/HistogramDataByDepartment",
  ImportAllProjectsAndOrders: "/api/Project/ImportAllProjectsAndOrders",
  SyncAllProjectsAndOrders: "/api/Project/SyncAllProjectsAndOrders",
  UnArchiveMainProject: "/api/Project/UnArchiveMainProject?mainprojectid=",
  ArchiveMainProject: "/api/Project/ArchiveMainProject?mainprojectid=",
  SyncDepartments: "/api/Resource/SyncDepartments",
  SyncEmployees: "/api/Resource/SyncEmployees",
  SyncAllResources: "/api/Resource/SyncAllResources",
  SyncAllVisibleProjectsAndOrders: "/api/Project/SyncAllVisibleProjectsAndOrders",
  UpdateTaskNote: "/api/Project/UpdateTaskNote",
  GetUsers: "/api/User/GetUsers",
  DeleteUser: "/api/User/DeleteUser",
  LoginUser: "/api/User/LoginUser",
  UpdateUser: "/api/User/UpdateUser",
};

export const parseParams = (queryParams) => {
  if (!queryParams) return "";
  const params = Object.keys(queryParams)
    .map((key) => {
      if (queryParams[key] === undefined) {
        return false;
      }
      const value = encodeURIComponent(queryParams[key]);
      if (value === "") {
        return false;
      }
      return `${key}=${value}`;
    })
    .filter((i) => i !== false);
  return params.length ? "?" + params.join("&") : "";
};
